import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import AgoraRTC, { useRTCClient } from "agora-rtc-react";
import type { IBufferSourceAudioTrack } from "agora-rtc-react";
import { uploadFile } from "../../../../services/api/uploadFile";
import {
  Box,
  IconButton,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AudioFile,
  Close,
  CloudUpload,
  GraphicEq,
  Loop,
  MusicNote,
  Pause,
  PlayArrow,
  SkipNext,
  SkipPrevious,
  VolumeDown,
  VolumeUp,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import "../../../../styles/sing4you-live.css";
import KaraokeLyrics from "./KaraokeLyrics";
import { useAuth, useCurrentTalentProfile } from "../../../../hooks";
import useCurrentBusinessInfo from "../../../../hooks/useCurrentBusiness";
import {
  useUploadLyrics,
  useUploadSing4YouLyrics,
} from "./apis/NewKarokeApi_updated";

/* ── Design tokens (Sing4You design system) ─────────────────── */
const ORANGE       = "#f97316";
const ORANGE_HOVER = "#ea6c0a";
const ORANGE_DIM   = "rgba(249,115,22,0.15)";
const GOLD         = "#fbbf24";

const sliderSx = {
  color: ORANGE,
  height: 4,
  "& .MuiSlider-thumb": {
    width: 13,
    height: 13,
    backgroundColor: GOLD,
    boxShadow: "0 0 8px rgba(251,191,36,0.7) !important",
    "&:hover": { boxShadow: `0 0 0 8px ${ORANGE_DIM}` },
  },
  "& .MuiSlider-track": {
    background: `linear-gradient(90deg, ${ORANGE}, #ef4444)`,
    border: "none",
  },
  "& .MuiSlider-rail": { backgroundColor: "rgba(255,255,255,0.12)" },
};

/* ─────────────────────────────────────────────────────────────── */
export interface AudioMixUpdatedRef {
  play: () => void;
  pause: () => void;
  stop: () => void;
  /**
   * Fully clear the loaded track: stop playback, unpublish, release resources,
   * and notify parent with duration 0 (so e.g. "no track loaded" UI is shown).
   * Use when the talent finishes or cancels karaoke.
   */
  clearTrack: () => void;
  seekTo: (seconds: number) => void;
  setVol: (value: number) => void;
  /** Load an audio URL directly (e.g. from the Song List) without opening the modal. */
  loadUrl: (url: string, label: string) => void;
  /**
   * Load a local File object directly into the audio mix without uploading it first.
   * Use when the talent picks a file on the main page — avoids CORS on the CDN entirely
   * and arms the play button immediately without opening the Karaoke Studio modal.
   */
  loadFile: (file: File, label: string) => void;
}

interface AudioMixProps {
  open: boolean;
  onClose: () => void;
  onLyricsChange?: (lrc: string | null) => void;
  onLyricsTimeChange?: (time: number) => void;
  onPlayStateChange?: (playing: boolean) => void;
  /** When true, the audio track keeps running after the modal closes (display:none only). */
  keepPlayingOnClose?: boolean;
  /** Called when a track finishes loading, with the track duration in seconds. */
  onDurationChange?: (seconds: number) => void;
  /**
   * Session identifier used for the lyrics upload endpoint.
   * Takes priority when `flashCardId` is absent from the URL (e.g. Sing4You routes
   * that only carry a `bookingId`). Accepts the numeric id as a string or number.
   */
  sessionId?: string | number;
}

type PlayState = "idle" | "loading" | "ready" | "playing" | "paused";

const VALID_AUDIO_TYPES = [
  "audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4",
  "audio/aac", "audio/webm", "audio/flac", "audio/x-m4a",
];
const VALID_AUDIO_EXT = /\.(mp3|wav|ogg|m4a|aac|webm|flac)$/i;

function isValidAudioFile(f: File): boolean {
  return VALID_AUDIO_TYPES.includes(f.type) || VALID_AUDIO_EXT.test(f.name);
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const AudioMixUpdatedComponent = React.forwardRef<AudioMixUpdatedRef, AudioMixProps>(
function AudioMixUpdatedInner({
  open,
  onClose,
  onLyricsChange,
  onLyricsTimeChange,
  onPlayStateChange,
  keepPlayingOnClose = false,
  onDurationChange,
  sessionId,
}: AudioMixProps, ref) {
  const client = useRTCClient();

  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const { currentBusiness } = useCurrentBusinessInfo();

  const [url, setUrl] = useState("");
  const [playState, setPlayState] = useState<PlayState>("idle");
  const [isLooping, setIsLooping] = useState(true);
  // Default 35 % → voice:music ratio ≈ 3:1 when mic is at 100.
  // Talent can raise this slider if the backing track is too quiet.
  const [volume, setVolume] = useState(35);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [songLabel, setSongLabel] = useState("");

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const trackRef = useRef<IBufferSourceAudioTrack | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPublishedRef = useRef(false);
  // Mirror of playState kept in a ref so imperative methods never have stale closures.
  const playStateRef = useRef<PlayState>("idle");
  // Set to true by handleStop/cleanup before calling stopProcessAudioBuffer() so the
  // source-state-change handler can tell a manual stop from a natural track end.
  const intentionalStopRef = useRef(false);

  // Separate local <audio> element so the talent hears the MP3 from their own
  // audio output device without routing it through the Agora buffer track's
  // .play() call.  The Agora track is only processed + published — never played
  // locally — so the mic cannot pick up the backing track from speakers.
  const localAudioRef    = useRef<HTMLAudioElement | null>(null);
  // When the source is a File we create an object URL; keep it here so we can
  // revoke it on cleanup to avoid memory leaks.
  const localObjectUrlRef = useRef<string>("");

  const [lyricsText, setLyricsText] = useState<string | null>(null);
  const [lyricsFileName, setLyricsFileName] = useState<string | null>(null);

  // Keep playStateRef current so imperative handle always reads fresh state.
  useEffect(() => { playStateRef.current = playState; }, [playState]);

  // Keep local audio loop flag in sync with the UI toggle.
  useEffect(() => {
    if (localAudioRef.current) localAudioRef.current.loop = isLooping;
  }, [isLooping]);

  // `flashCardId` from URL → flash-card endpoint.
  // `sessionId` prop without a flashCardId in URL → Sing4You booking endpoint.
  const flashCardIdFromUrl = Number(
    new URLSearchParams(window.location.search).get("flashCardId"),
  ) || 0;
  const isSing4You      = !flashCardIdFromUrl && !!Number(sessionId);
  const resolvedUploadId = flashCardIdFromUrl || Number(sessionId) || 0;

  const talentId =
    currentProfile?.id?.toString() ||
    currentBusiness?.id?.toString() ||
    userInfo?.id?.toString() ||
    "";

  // Both hooks always mounted (React rules); only one mutate is ever called.
  const { mutate: _uploadToFlashCard } = useUploadLyrics({
    key: ["upload-lyrics-flash-card"],
    params: { id: resolvedUploadId, headers: { "talent-id": talentId } },
  });
  const { mutate: _uploadToBooking } = useUploadSing4YouLyrics({
    key: ["upload-lyrics-sing4you"],
    params: { id: resolvedUploadId, headers: { "talent-id": talentId } },
  });

  // Alias — pick the right backend based on context.
  const uploadLyricsToBackend = isSing4You ? _uploadToBooking : _uploadToFlashCard;

  // Full cleanup: stop processing, unpublish, close track, teardown local audio.
  // Pass resetState=false when the caller will set its own state (e.g. during a reload).
  const cleanup = async (resetState = true) => {
    stopProgressTimer();

    // Stop local audio playback and release resources.
    if (localAudioRef.current) {
      localAudioRef.current.pause();
      localAudioRef.current.src = "";
      localAudioRef.current = null;
    }
    if (localObjectUrlRef.current) {
      URL.revokeObjectURL(localObjectUrlRef.current);
      localObjectUrlRef.current = "";
    }

    if (trackRef.current) {
      intentionalStopRef.current = true; // suppress natural-end callbacks during cleanup
      try {
        // Some browsers throw InvalidStateError if stop is called before start.
        try {
          trackRef.current.stopProcessAudioBuffer();
        } catch (err: any) {
          if (
            err?.name !== "InvalidStateError" &&
            !/cannot call stop without calling start/i.test(err?.message || "")
          ) {
            throw err;
          }
          // Swallow benign "stop without start" errors.
          console.warn(
            "[AudioMix] Ignoring stopProcessAudioBuffer InvalidStateError during cleanup"
          );
        }
        if (isPublishedRef.current && client.connectionState === "CONNECTED") {
          await client.unpublish([trackRef.current as any]);
        }
        isPublishedRef.current = false;
        trackRef.current.close();
      } catch (err) {
        console.warn("[AudioMix] Cleanup error (non-critical):", err);
        isPublishedRef.current = false;
      }
      trackRef.current = null;
    }
    if (resetState) {
      setPlayState("idle");
      setCurrentTime(0);
      setDuration(0);
      onPlayStateChange?.(false);
    }
  };

  // Effect 1 — stop the track when the modal closes WITHOUT keepPlayingOnClose.
  // This does NOT run a cleanup return, so the track is never accidentally stopped
  // when dependencies change.
  useEffect(() => {
    if (!open && !keepPlayingOnClose) {
      cleanup(true);
      setSelectedFile(null);
      setIsUploading(false);
      setUploadProgress(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, keepPlayingOnClose]);

  // Effect 2 — final teardown only on component unmount (empty deps).
  // Separated so it never fires when open/keepPlayingOnClose change mid-session.
  useEffect(() => {
    return () => {
      cleanup(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startProgressTimer = () => {
    stopProgressTimer();
    progressTimerRef.current = setInterval(() => {
      if (trackRef.current) {
        const t = trackRef.current.getCurrentTime();
        setCurrentTime(t);
        onLyricsTimeChange?.(t);
        setDuration(trackRef.current.duration);
      }
    }, 100); // 100 ms → lyric lines switch within 0.1 s of their timestamp
  };

  const stopProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  // Core load logic — accepts a URL string or a File object.
  // Passing a File avoids CORS issues with hosted URLs entirely.
  const loadSource = async (source: string | File, label: string) => {
    setPlayState("loading");
    setCurrentTime(0);
    setDuration(0);

    try {
      await cleanup(false);

      // ── Agora buffer track (for processing + publishing to viewers) ──────
      const track = await AgoraRTC.createBufferSourceAudioTrack({ source } as any);
      // setVolume controls the gain of the published stream (what viewers hear).
      track.setVolume(volume);

      (track as any).on("source-state-change", (state: string) => {
        if (state === "stopped") {
          stopProgressTimer();
          isPublishedRef.current = false;
          setPlayState("ready");
          setCurrentTime(0);

          // Also stop the local audio so it doesn't keep playing after the
          // published stream ends (e.g. a non-looping track reaches its end).
          if (localAudioRef.current) {
            localAudioRef.current.pause();
            localAudioRef.current.currentTime = 0;
          }

          // Natural end of track (not a manual stop / cleanup call).
          if (!intentionalStopRef.current) {
            onPlayStateChange?.(false);
            onLyricsChange?.(null);
            setLyricsText(null);
            setLyricsFileName(null);
          }
          intentionalStopRef.current = false;
        }
      });

      trackRef.current = track;

      // ── Local <audio> element (for talent's own ears only) ───────────────
      // The Agora track's .play() is intentionally NOT called — calling it
      // routes the audio through Agora's internal decoder to the default output
      // device, which means the mic can pick it up from speakers and re-inject
      // it into the published stream as feedback/echo.
      // Instead we play the same source through a standard HTMLAudioElement so
      // the talent hears the backing track while the Agora track remains
      // purely a processing+publish pipeline.
      let localSrc: string;
      if (source instanceof File) {
        // Revoke any previous object URL first.
        if (localObjectUrlRef.current) URL.revokeObjectURL(localObjectUrlRef.current);
        localSrc = URL.createObjectURL(source);
        localObjectUrlRef.current = localSrc;
      } else {
        localObjectUrlRef.current = "";
        localSrc = source;
      }
      const localAudio = new Audio(localSrc);
      localAudio.volume = volume / 100;
      localAudio.loop   = isLooping;
      localAudioRef.current = localAudio;

      setDuration(track.duration);
      onDurationChange?.(track.duration);
      setSongLabel(label);
      setPlayState("ready");
      enqueueSnackbar("Audio loaded — press Play to start mixing", {
        variant: "success",
      });
    } catch (err: any) {
      console.error("[AudioMix] Failed to load track:", err);
      enqueueSnackbar(
        err?.message || "Failed to load audio. Check the URL and CORS settings.",
        { variant: "error" }
      );
      setPlayState("idle");
    }
  };

  const handleLoad = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      enqueueSnackbar("Please enter an audio file URL", { variant: "warning" });
      return;
    }
    try {
      const parts = trimmedUrl.split("/");
      const filename = decodeURIComponent(parts[parts.length - 1].split("?")[0]);
      await loadSource(trimmedUrl, filename || "Background Music");
    } catch {
      await loadSource(trimmedUrl, "Background Music");
    }
  };

  // File picker handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isValidAudioFile(file)) {
      enqueueSnackbar("Invalid file type. Use MP3, WAV, OGG, M4A, AAC, or FLAC.", {
        variant: "error",
      });
      return;
    }
    setSelectedFile(file);
    // Reset the input so the same file can be picked again
    e.target.value = "";
  };

  const handleUploadAndLoad = async () => {
    if (!selectedFile) return;

    // Keep a reference to the File now — we'll pass it directly to Agora
    // so we never need to re-fetch the hosted URL (avoids CORS on the CDN/uploads endpoint).
    const fileToLoad = selectedFile;
    const label = selectedFile.name;

    setIsUploading(true);
    setUploadProgress(0);

    // Fake progress ticker (same pattern as Sing4YouManagement_updated)
    uploadTickRef.current = setInterval(
      () => setUploadProgress((p) => (p < 85 ? p + 8 : p)),
      350
    );

    try {
      // Upload to get a persistent URL (stored for reference / future playback)
      const uploadedUrl = await uploadFile(selectedFile);
      clearInterval(uploadTickRef.current!);
      setUploadProgress(100);

      setSelectedFile(null);
      setIsUploading(false);
      setUploadProgress(0);
      setUrl(uploadedUrl); // Store URL for display / manual reload

      // Load from the original File object — no CORS needed
      await loadSource(fileToLoad, label);
    } catch (err: any) {
      clearInterval(uploadTickRef.current!);
      setIsUploading(false);
      setUploadProgress(0);
      enqueueSnackbar(
        err?.message || "Upload failed. Please try again.",
        { variant: "error" }
      );
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
  };

  const handleLyricsFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      if (!text.trim()) {
        enqueueSnackbar("Lyrics file appears to be empty.", {
          variant: "warning",
        });
        return;
      }
      setLyricsText(text);
      setLyricsFileName(file.name);
      onLyricsChange?.(text);
      enqueueSnackbar("Lyrics loaded. They will follow the song timing.", {
        variant: "success",
      });
    };
    reader.onerror = () => {
      enqueueSnackbar("Failed to read lyrics file.", {
        variant: "error",
      });
    };
    reader.readAsText(file);

    // Also upload lyrics file to backend so viewers can receive URL via websocket
    try {
      if (!resolvedUploadId || Number.isNaN(resolvedUploadId)) {
        console.warn(
          "[AudioMix] No valid sessionId / flashCardId available; skipping lyrics upload API.",
        );
      } else {
        const lyricsUrl = await uploadFile(file);

        if (!lyricsUrl) {
          enqueueSnackbar("Failed to upload lyrics to server.", {
            variant: "error",
          });
        } else {
          uploadLyricsToBackend(
            {
              business_id: currentBusiness?.id ?? 0,
              lyrics_url: lyricsUrl,
            },
            {
              onError: (error: any) => {
                console.error("[AudioMix] Failed to post lyrics URL:", error);
                enqueueSnackbar("Failed to notify viewers about lyrics.", {
                  variant: "error",
                });
              },
            },
          );
        }
      }
    } catch (error) {
      console.error("[AudioMix] Lyrics upload error:", error);
      enqueueSnackbar("Failed to upload lyrics file to server.", {
        variant: "error",
      });
    }

    e.target.value = "";
  };

  const handlePlay = async () => {
    if (!trackRef.current) return;

    if (playState === "paused") {
      trackRef.current.resumeProcessAudioBuffer();
      // Resume local audio so talent hears the track continue from the same position.
      localAudioRef.current?.play().catch(() => {});
      setPlayState("playing");
      onPlayStateChange?.(true);
      startProgressTimer();
      return;
    }

    // Guard: channel must be joined before we can publish
    if (client.connectionState !== "CONNECTED") {
      enqueueSnackbar(
        "Stream not connected yet — please wait a moment and try again.",
        { variant: "warning" }
      );
      return;
    }

    try {
      // Process + publish for viewers — do NOT call .play() on the Agora track.
      // Calling .play() would route the decoded audio to the system output,
      // allowing the mic to re-capture it from speakers and inject it as echo
      // into the published stream.
      trackRef.current.startProcessAudioBuffer({ loop: isLooping });
      await client.publish([trackRef.current as any]);
      isPublishedRef.current = true;

      // Play locally for the talent's ears only via the HTMLAudioElement.
      localAudioRef.current?.play().catch(() => {});

      setPlayState("playing");
      onPlayStateChange?.(true);
      startProgressTimer();
    } catch (err: any) {
      console.error("[AudioMix] Failed to publish track:", err);
      enqueueSnackbar("Failed to start audio mixing", { variant: "error" });
    }
  };

  const handlePause = () => {
    if (!trackRef.current || playState !== "playing") return;
    try {
      trackRef.current.pauseProcessAudioBuffer();
    } catch (err: any) {
      if (
        err?.name !== "InvalidStateError" &&
        !/cannot call stop without calling start/i.test(err?.message || "")
      ) {
        throw err;
      }
      console.warn(
        "[AudioMix] Ignoring pauseProcessAudioBuffer InvalidStateError (likely paused before start)",
        err
      );
    }
    localAudioRef.current?.pause();
    stopProgressTimer();
    setPlayState("paused");
    onPlayStateChange?.(false);
  };

  const handleStop = async () => {
    if (!trackRef.current) return;
    stopProgressTimer();
    if (localAudioRef.current) {
      localAudioRef.current.pause();
      localAudioRef.current.currentTime = 0;
    }
    intentionalStopRef.current = true; // suppress natural-end callbacks
    try {
      trackRef.current.stopProcessAudioBuffer();
    } catch (err: any) {
      if (
        err?.name !== "InvalidStateError" &&
        !/cannot call stop without calling start/i.test(err?.message || "")
      ) {
        throw err;
      }
      console.warn(
        "[AudioMix] Ignoring stopProcessAudioBuffer InvalidStateError in handleStop",
        err
      );
    }
    if (isPublishedRef.current) {
      try {
        if (client.connectionState === "CONNECTED") {
          await client.unpublish([trackRef.current as any]);
        }
      } catch (err) {
        console.warn("[AudioMix] Unpublish error (non-critical):", err);
      }
      isPublishedRef.current = false;
    }
    setPlayState("ready");
    setCurrentTime(0);
    onPlayStateChange?.(false);
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const v = value as number;
    setVolume(v);
    if (trackRef.current) trackRef.current.setVolume(v);
    if (localAudioRef.current) localAudioRef.current.volume = v / 100;
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    const seconds = value as number;
    if (trackRef.current) {
      trackRef.current.seekAudioBuffer(seconds);
      setCurrentTime(seconds);
    }
    if (localAudioRef.current) localAudioRef.current.currentTime = seconds;
  };

  // ── Imperative API exposed to parent via ref ────────────────
  useImperativeHandle(ref, () => ({
    play: () => {
      if (!trackRef.current) return;
      if (playStateRef.current === "paused") {
        trackRef.current.resumeProcessAudioBuffer();
        localAudioRef.current?.play().catch(() => {});
        setPlayState("playing");
        onPlayStateChange?.(true);
        startProgressTimer();
        return;
      }
      if (client.connectionState !== "CONNECTED") {
        enqueueSnackbar("Stream not connected yet — please wait and try again.", { variant: "warning" });
        return;
      }
      try {
        // Process + publish for viewers only — no .play() on the Agora track.
        trackRef.current.startProcessAudioBuffer({ loop: isLooping });
        client.publish([trackRef.current as any]).then(() => {
          isPublishedRef.current = true;
        }).catch((err: any) => {
          console.error("[AudioMix] Publish error:", err);
          enqueueSnackbar("Failed to start audio mixing", { variant: "error" });
        });
        // Play locally for the talent's ears only.
        localAudioRef.current?.play().catch(() => {});
        setPlayState("playing");
        onPlayStateChange?.(true);
        startProgressTimer();
      } catch (err: any) {
        console.error("[AudioMix] Failed to start track:", err);
        enqueueSnackbar("Failed to start audio mixing", { variant: "error" });
      }
    },
    pause: () => {
      if (!trackRef.current || playStateRef.current !== "playing") return;
      try {
        trackRef.current.pauseProcessAudioBuffer();
      } catch (err: any) {
        if (err?.name !== "InvalidStateError" && !/cannot call stop without calling start/i.test(err?.message || "")) throw err;
      }
      localAudioRef.current?.pause();
      stopProgressTimer();
      setPlayState("paused");
      onPlayStateChange?.(false);
    },
    stop: () => {
      handleStop();
    },
    clearTrack: () => {
      cleanup(true).then(() => {
        onDurationChange?.(0);
      });
    },
    seekTo: (seconds: number) => {
      if (trackRef.current) {
        trackRef.current.seekAudioBuffer(seconds);
        setCurrentTime(seconds);
        onLyricsTimeChange?.(seconds);
      }
      if (localAudioRef.current) localAudioRef.current.currentTime = seconds;
    },
    setVol: (v: number) => {
      setVolume(v);
      trackRef.current?.setVolume(v);
      if (localAudioRef.current) localAudioRef.current.volume = v / 100;
    },
    loadUrl: (url: string, label: string) => {
      loadSource(url, label);
    },
    loadFile: (file: File, label: string) => {
      loadSource(file, label);
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isLooping, client]);

  // When keepPlayingOnClose, just hide with CSS so the track keeps running.
  if (!open && !keepPlayingOnClose) return null;

  const isBusy      = playState === "loading" || isUploading;
  const isLoading   = playState === "loading";
  const isReady     = playState === "ready";
  const isPlaying   = playState === "playing";
  const isPaused    = playState === "paused";
  const hasTrack    = isReady || isPlaying || isPaused;
  const isConnected = client.connectionState === "CONNECTED";

  const statusColor = isPlaying             ? "#22C55E"
    : isPaused                              ? "#F59E0B"
    : isReady                               ? "#60A5FA"
    : isUploading || isLoading              ? ORANGE
    : "rgba(255,255,255,0.25)";

  const statusText = isPlaying              ? "Mixing — audience can hear background music"
    : isPaused                              ? "Paused"
    : isReady && !isConnected               ? "Waiting for stream to connect…"
    : isReady                               ? "Ready — press Play to start mixing"
    : isUploading                           ? "Uploading audio file…"
    : isLoading                             ? "Loading audio…"
    : "Upload a file or paste a URL to start";

  return (
    <Box style={{ display: (!open && keepPlayingOnClose) ? "none" : undefined }}>
    <Box
      className="audiomix-root"
      sx={{
        width: "100%",
        backgroundColor: "#0a0d1f",
        backdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 3,
        boxShadow: "0 16px 40px rgba(0,0,0,0.7)",
        p: 2.5,
        maxHeight: { xs: "70vh", md: "78vh" },
        overflowY: "auto",
        fontFamily: "var(--primary-font-family, 'Lexend', sans-serif)",
      }}
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <GraphicEq sx={{ color: ORANGE, fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            Karaoke Studio
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.55)" }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      {/* ── Upload drop zone ────────────────────────────────── */}
      {!isUploading && !selectedFile && (
        <>
          <input
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            style={{ display: "none" }}
            id="audiomix-file-input"
            type="file"
            onChange={handleFileChange}
            disabled={isBusy}
          />
          <label htmlFor="audiomix-file-input">
            <Box
              component="span"
              sx={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                py: 1.1, borderRadius: 2,
                border: "1px dashed rgba(255,255,255,0.18)",
                cursor: isBusy ? "not-allowed" : "pointer",
                backgroundColor: "rgba(255,255,255,0.03)",
                color: "rgba(255,255,255,0.55)", fontSize: 13, fontWeight: 500,
                transition: "all 0.2s", mb: 1.5,
                "&:hover": {
                  borderColor: ORANGE, color: "#fff",
                  backgroundColor: ORANGE_DIM,
                },
              }}
            >
              <CloudUpload sx={{ fontSize: 18, color: ORANGE }} />
              Choose MP3 / Audio File
            </Box>
          </label>

          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.08)" }} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.28)", fontSize: 11 }}>
              or paste URL
            </Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.08)" }} />
          </Stack>
        </>
      )}

      {/* ── File selected ───────────────────────────────────── */}
      {selectedFile && !isUploading && (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            p: 1.5, mb: 1.5, borderRadius: 2,
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.09)",
          }}
        >
          <AudioFile sx={{ color: ORANGE, fontSize: 26, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.38)", fontSize: 11 }}>
              {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
            </Typography>
          </Box>
          <Stack direction="row" gap={0.5}>
            <Box
              component="button"
              onClick={handleUploadAndLoad}
              sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1.5, py: 0.75, borderRadius: 1.5, border: "none",
                backgroundColor: ORANGE, color: "#fff",
                fontWeight: 600, fontSize: 12, cursor: "pointer",
                "&:hover": { backgroundColor: ORANGE_HOVER },
              }}
            >
              <CloudUpload sx={{ fontSize: 14 }} /> Upload
            </Box>
            <IconButton
              size="small"
              onClick={handleCancelFile}
              sx={{ color: "rgba(255,255,255,0.38)", "&:hover": { color: "#fff" } }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* ── Upload progress ─────────────────────────────────── */}
      {isUploading && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <CloudUpload sx={{ color: ORANGE, fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12, flex: 1 }} noWrap>
              Uploading {selectedFile?.name ?? "file"}…
            </Typography>
            <Typography variant="caption" sx={{ color: ORANGE, fontWeight: 700, fontSize: 12 }}>
              {uploadProgress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              borderRadius: 2, height: 5,
              backgroundColor: "rgba(255,255,255,0.07)",
              "& .MuiLinearProgress-bar": { borderRadius: 2, backgroundColor: ORANGE },
            }}
          />
        </Box>
      )}

      {/* ── URL input ───────────────────────────────────────── */}
      {!isUploading && !selectedFile && (
        <Stack direction="row" gap={1} mb={2}>
          <TextField
            fullWidth size="small"
            placeholder="https://… (MP3, AAC, WAV)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !isBusy && handleLoad()}
            disabled={isBusy}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff", fontSize: 13,
                backgroundColor: "rgba(255,255,255,0.05)", borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.13)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.28)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
              },
              "& input::placeholder": { color: "rgba(255,255,255,0.28)", fontSize: 12 },
            }}
          />
          <Box
            component="button"
            onClick={handleLoad}
            disabled={isBusy}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: 58, px: 1.5, borderRadius: 2, border: "none",
              cursor: isBusy ? "not-allowed" : "pointer",
              backgroundColor: isBusy ? "rgba(255,255,255,0.08)" : ORANGE,
              color: "#fff", fontWeight: 600, fontSize: 13,
              "&:hover:not(:disabled)": { backgroundColor: ORANGE_HOVER },
            }}
          >
            {isLoading ? "…" : "Load"}
          </Box>
        </Stack>
      )}

      {/* ── Song label + lyrics file name ───────────────────── */}
      {hasTrack && songLabel && (
        <Stack direction="row" alignItems="center" gap={0.75} mb={1.5} flexWrap="wrap">
          <MusicNote sx={{ fontSize: 14, color: ORANGE }} />
          <Typography variant="caption" noWrap sx={{ color: "rgba(255,255,255,0.65)", maxWidth: 200, fontSize: 12 }}>
            {songLabel}
          </Typography>
          {lyricsFileName && (
            <Typography variant="caption" sx={{ color: GOLD, fontSize: 10, opacity: 0.75 }}>
              · {lyricsFileName}
            </Typography>
          )}
        </Stack>
      )}

      {/* ── Lyrics upload (.lrc) ─────────────────────────────── */}
      {hasTrack && (
        <Box sx={{ mb: 1.5 }}>
          <input
            accept=".lrc,text/plain"
            style={{ display: "none" }}
            id="audiomix-lyrics-input"
            type="file"
            onChange={handleLyricsFileChange}
          />
          <label htmlFor="audiomix-lyrics-input">
            <Box
              component="span"
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                px: 1.25, py: 0.5, borderRadius: 999,
                border: `1px solid rgba(251,191,36,0.35)`,
                cursor: "pointer",
                backgroundColor: "rgba(251,191,36,0.05)",
                color: GOLD, fontSize: 11, fontWeight: 600,
                transition: "all 0.18s",
                "&:hover": {
                  borderColor: GOLD,
                  backgroundColor: "rgba(251,191,36,0.12)",
                },
              }}
            >
              <MusicNote sx={{ fontSize: 13, color: GOLD }} />
              {lyricsText ? "Change lyrics (.lrc)" : "Upload lyrics (.lrc)"}
            </Box>
          </label>
        </Box>
      )}

      {/* ── Seek bar ────────────────────────────────────────── */}
      {hasTrack && duration > 0 && (
        <Box mb={0.5}>
          <Slider
            value={currentTime}
            min={0}
            max={duration}
            step={1}
            onChange={handleSeek}
            sx={sliderSx}
          />
          <Stack direction="row" justifyContent="space-between" mt={-0.5}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
              {formatTime(duration)}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── Playback controls: [⏮] [large play] [⏭] [loop] ── */}
      <Stack direction="row" alignItems="center" justifyContent="center" gap={1} mt={hasTrack ? 1 : 0} mb={1.5}>

        {/* ⏮ Restart */}
        <Tooltip title="Restart" arrow>
          <span>
            <IconButton
              onClick={handleStop}
              disabled={!hasTrack}
              sx={{
                color: hasTrack ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.2)",
                width: 36, height: 36,
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <SkipPrevious />
            </IconButton>
          </span>
        </Tooltip>

        {/* ▶ / ⏸  large play */}
        <Tooltip
          title={
            !isConnected    ? "Waiting for stream connection…"
            : isPlaying     ? "Pause"
            : isPaused      ? "Resume"
            : "Play"
          }
          arrow
        >
          <span>
            <IconButton
              onClick={isPlaying ? handlePause : handlePlay}
              disabled={!hasTrack || !isConnected}
              sx={{
                backgroundColor: ORANGE, color: "#fff", width: 48, height: 48,
                boxShadow: hasTrack ? "0 0 18px rgba(249,115,22,0.5)" : "none",
                "&:hover": { backgroundColor: ORANGE_HOVER, boxShadow: "0 0 26px rgba(249,115,22,0.65)" },
                "&:disabled": { backgroundColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)", boxShadow: "none" },
              }}
            >
              {isPlaying ? <Pause sx={{ fontSize: 22 }} /> : <PlayArrow sx={{ fontSize: 22 }} />}
            </IconButton>
          </span>
        </Tooltip>

        {/* ⏭ Next (queue placeholder) */}
        <Tooltip title="Next (queue)" arrow>
          <span>
            <IconButton
              disabled={!hasTrack}
              sx={{
                color: hasTrack ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.2)",
                width: 36, height: 36,
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <SkipNext />
            </IconButton>
          </span>
        </Tooltip>

        {/* 🔁 Loop */}
        <Tooltip title={isLooping ? "Loop: ON" : "Loop: OFF"} arrow>
          <IconButton
            onClick={() => setIsLooping((p) => !p)}
            sx={{
              backgroundColor: isLooping ? ORANGE_DIM : "rgba(255,255,255,0.07)",
              color: isLooping ? ORANGE : "rgba(255,255,255,0.45)",
              width: 36, height: 36,
              border: isLooping ? "1px solid rgba(249,115,22,0.4)" : "1px solid rgba(255,255,255,0.09)",
              "&:hover": { backgroundColor: isLooping ? "rgba(249,115,22,0.25)" : "rgba(255,255,255,0.12)" },
            }}
          >
            <Loop sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Music Volume ────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" gap={0.5} mt={0.5} mb={0.25}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Music Volume
        </Typography>
        <Typography variant="caption" sx={{
          fontSize: 10, fontWeight: 700, ml: 0.5,
          color: volume <= 50 ? "#22c55e" : volume <= 70 ? "#f59e0b" : "#ef4444",
        }}>
          {volume <= 50 ? "✓ Karaoke" : volume <= 70 ? "⚠ Loud" : "⚠ Overpowers voice"}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <VolumeDown sx={{ color: "rgba(255,255,255,0.45)", fontSize: 18, flexShrink: 0 }} />
        <Slider
          value={volume}
          min={0}
          max={100}
          onChange={handleVolumeChange}
          sx={sliderSx}
        />
        <VolumeUp sx={{ color: "rgba(255,255,255,0.45)", fontSize: 18, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", minWidth: 28, textAlign: "right", fontSize: 11 }}>
          {volume}%
        </Typography>
      </Stack>

      {/* ── Status row ──────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" gap={0.75} mt={1.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, backgroundColor: statusColor }} />
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
          {statusText}
        </Typography>
      </Stack>
    </Box>
    </Box>
  );
});

export default AudioMixUpdatedComponent;
